import classNames from "classnames";
import get from "lodash/get";
import * as React from "react";
import { devLog } from "utils/devtools/devLogging";

import { useStyles } from "./styles";

export type TVariant = "button" | "h1" | "h2" | "h3" | "sectionTitle" | "small" | "standard" | "tiny";

interface ITypographyProps {
  component?: keyof React.JSX.IntrinsicElements;
  fade?: boolean;
  multiLine?: boolean;
  noWrap?: boolean;
  prefixIcon?: React.ReactNode;
  relative?: boolean;
  variant?: TVariant;
}
type TTypographyProps = ITypographyProps & React.HTMLAttributes<HTMLElement>;

export const Typography: FC<TTypographyProps> = ({
  children,
  className,
  component = null,
  fade = false,
  multiLine = false,
  noWrap = false,
  prefixIcon = null,
  ref,
  relative = false,
  variant = "standard",
  ...componentProps
}) => {
  const classes = useStyles();
  const componentType = component || variantToComponentNameMapping.get(variant) || "div";
  const finalChildren = prefixIcon
    ? (
        <>
          {prefixIcon}
          {children}
        </>
      )
    : (
        children
      );
  // eslint-disable-next-line react-hooks/refs -- ref is only forwarded into createElement by the helper, never read during render
  return componentNameToComponent(componentType, {
    children: finalChildren,
    className: classNames(
      classes.base,
      get(classes, variant),
      {
        [classes.fade]: fade,
        [classes.multiLine]: multiLine,
        [classes.noWrap]: noWrap,
        [classes.prefixIcon]: !!prefixIcon,
        [classes.relative]: relative,
      },
      className,
    ),
    ref: ref as React.Ref<HTMLElement>,
    ...componentProps,
  });
};

const variantToComponentNameMapping: Map<TVariant, keyof React.JSX.IntrinsicElements> = new Map([
  ["button", "div"],
  ["h1", "h1"],
  ["h2", "h2"],
  ["h3", "h3"],
  ["sectionTitle", "h3"],
  ["small", "div"],
  ["standard", "div"],
  ["tiny", "div"],
]);

const componentNameToComponent = (
  componentName: keyof React.JSX.IntrinsicElements,
  props: React.HTMLAttributes<HTMLElement> & { ref: React.Ref<HTMLElement> },
) => {
  try {
    return React.createElement(componentName, props);
  } catch (error) {
    devLog({ extra: { componentName, error, props }, level: "warn", message: "Error creating component" });
    return React.createElement("p", props);
  }
};
